import { useEffect, useMemo, useState } from 'react';

import type { BISettings } from './../../ricos-common/RicosTypes.js';
import { RICOS_VERSION } from './../../ricos-schema/next-schema/index.js';
import type { BICallbacks } from './../../ricos-types/index.js';
import { parseInstance } from './../request-service-provider/parse-instance.js';

import type { BiStrategyResult } from './index.js';

type BiHelpersState = {
  helpers: Partial<BICallbacks>;
  biLogger?: BiStrategyResult['biLogger'];
  loading: boolean;
};

type UseBiHelpersParams = {
  instance: string | undefined;
  biSettings: BISettings | undefined;
  msid?: string;
};

type UseBiHelpersResult = BiHelpersState & {
  biLoadPromise: Promise<Partial<BICallbacks>>;
};

/**
 * React hook that lazy loads BI helpers when instance and biSettings are provided.
 * Returns empty helpers immediately, then updates once the BI module is loaded.
 * The returned `biLoadPromise` resolves with all BI callbacks once loaded.
 */
export const useBiHelpers = ({
  instance,
  biSettings,
  msid = '',
}: UseBiHelpersParams): UseBiHelpersResult => {
  const shouldLoadBi = !!instance && !!biSettings;

  const [state, setState] = useState<BiHelpersState>({
    helpers: {},
    loading: shouldLoadBi,
  });

  // Parse instance once
  const { appDefId, metaSiteId: instanceMsid } = useMemo(
    () => (instance ? parseInstance(instance) : { appDefId: '', metaSiteId: '' }),
    [instance]
  );
  const metaSiteId = instanceMsid || msid;

  // Create the deferred promise during render so it's available before effects run.
  // Keyed on the same deps as the loading effect so a new promise is created when deps change.
  const [biLoadDeferred] = useState(() => {
    if (!shouldLoadBi) {
      return { promise: Promise.resolve({} as BICallbacks), resolve: (_h: BICallbacks) => {} };
    }
    let resolve!: (helpers: BICallbacks) => void;
    const promise = new Promise<BICallbacks>(r => {
      resolve = r;
    });
    return { promise, resolve };
  });

  useEffect(() => {
    if (!shouldLoadBi || !biSettings) {
      setState({ helpers: {}, loading: false });
      return;
    }

    let cancelled = false;

    import('./index.js')
      .then(({ createBiHelpers }) =>
        createBiHelpers({
          appDefId,
          metaSiteId,
          biSettings,
          wixRicosVersion: RICOS_VERSION,
          ricosVersion: RICOS_VERSION,
        })
      )
      .then(result => {
        if (!cancelled) {
          setState({
            helpers: result.helpers,
            biLogger: result.biLogger,
            loading: false,
          });
          biLoadDeferred.resolve(result.helpers);
        }
      })
      .catch(error => {
        console.error('[useBiHelpers] Failed to load BI helpers:', error);
        if (!cancelled) {
          setState({ helpers: {}, loading: false });
          biLoadDeferred.resolve({} as BICallbacks);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shouldLoadBi, appDefId, metaSiteId, biSettings, biLoadDeferred]);

  return { ...state, biLoadPromise: biLoadDeferred.promise };
};
